import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { formatTimeAgo } from "@/zapi-frontend/utils/dates";
import { TNotification } from "../model/store";
import { CheckCircleIcon, ChatCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";

type TNotificationItemProps = {
  notification: TNotification;
  onMarkAsRead: (id: number) => void;
  onClose?: () => void;
};

const PRESENTATION_TYPES = ["outline_ready", "slides_ready", "outline_outdated"] as const;

export function NotificationItem({ notification, onMarkAsRead, onClose }: TNotificationItemProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const getNotificationContent = () => {
    const chapterName = notification.projectChapter?.projectTemplateChapter?.name;
    const chapter = chapterName || t("notifications.unknownChapter");

    switch (notification.type) {
      case "chapter_ready":
        return {
          title: t("notifications.chapterReady", { chapter }),
          subtitle: "",
          icon: CheckCircleIcon
        };
      case "chat_responded":
        return {
          title: t("notifications.chatResponded", { chapter }),
          subtitle: "",
          icon: ChatCircleIcon
        };
      case "outline_ready":
        return {
          title: t("notifications.outlineReady"),
          subtitle: "",
          icon: CheckCircleIcon
        };
      case "slides_ready":
        return {
          title: t("notifications.slidesReady"),
          subtitle: "",
          icon: CheckCircleIcon
        };
      case "outline_outdated":
        return {
          title: t("notifications.outlineOutdated"),
          subtitle: "",
          icon: WarningIcon
        };
      default:
        return {
          title: t("notifications.newNotification"),
          subtitle: t("notifications.newNotificationSubtitle"),
          icon: CheckCircleIcon
        };
    }
  };

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    const isPresentationType = (PRESENTATION_TYPES as readonly string[]).includes(notification.type);

    if (isPresentationType) {
      const projectId = notification.projectId ?? notification.project?.id;
      if (projectId) {
        navigate(`${getRouteProjectDetails(projectId)}?tab=presentation`);
      }
    } else {
      const projectId = notification.projectChapter?.projectId;
      if (projectId) {
        navigate(`${getRouteProjectDetails(projectId)}?tab=document&chapterId=${notification.projectChapterId}`, {
          replace: false,
          state: { chapterId: notification.projectChapterId }
        });
      }
    }

    onClose?.();
  };

  const content = getNotificationContent();
  const Icon = content.icon;

  return (
    <div
      className={cn(
        "pl-6 pr-3 py-4 ml-3 transition-colors cursor-pointer hover:bg-innoveraZinc-100/50 border-l-2",
        notification.isRead ? "border-transparent" : "border-innoveraOrange-400"
      )}
      onClick={handleClick}
    >
      <div className="flex gap-2 items-center">
        <div className="flex-shrink-0">
          <Icon
            size={16}
            weight={notification.isRead ? "regular" : notification.type === "outline_outdated" ? "regular" : "fill"}
            className={cn(
              notification.isRead
                ? "text-innoveraZinc-400"
                : notification.type === "outline_outdated"
                ? "text-innoveraZinc-900"
                : "text-innoveraOrange-400"
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <FigmaTypography
              variant="label-2"
              className={cn("leading-5", notification.isRead ? "text-innoveraZinc-600" : "text-innoveraZinc-900")}
            >
              {content.title}
            </FigmaTypography>
            <FigmaTypography variant="body-3" color="zinc400" className="whitespace-nowrap flex-shrink-0">
              {formatTimeAgo(notification.createdAt)}
            </FigmaTypography>
          </div>
          {content.subtitle && (
            <FigmaTypography variant="body-2" className="text-innoveraZinc-500 leading-5 mt-0.5 text-[14px]">
              {content.subtitle}
            </FigmaTypography>
          )}
        </div>
      </div>
    </div>
  );
}
